<template>
  <div>
    <SearchWithMultiSelectLegacy
      :class="errorStyle"
      data-test-id="search"
      :loadingOptions="loading"
      :options="options"
      :searchText="searchText"
      :totalOptions="getTotalCount"
      :value="selectedValues"
      @filter="handleIncludedUsersFilter"
      @input="handleIncludedUsersInput"
    />
    <MessageContainer
      v-if="containsDeletedGroups"
      :class="$style.message"
      level="warn"
    >
      One or more user groups have been deleted from your organization, and they'll be
      removed from this policy when you save it.
    </MessageContainer>

    <div :class="$style.error">
      <transition :name="$style.fade">
        <span v-if="$validator.errors.any()">
          {{ errorMessage }}
        </span>
      </transition>
    </div>
  </div>
</template>

<script>
import {
  ItemTypeConsts,
  StoreModuleMap,
} from 'client/util/Constants';
import { debounce } from 'lodash';
import { getMessageForError } from 'client/components/FormValidation/helpers/validationHelpers';
import { mapActions, mapGetters } from 'vuex';
import MessageContainer from 'client/components/MessageContainer/MessageContainer';
import SearchWithMultiSelectLegacy from 'client/components/SearchWithMultiSelect/SearchWithMultiSelectLegacy.vue';

const mapUserGroupToOption = ({ id, name }) => ({ name, value: id });

const userGroupCollection = StoreModuleMap[ItemTypeConsts.userGroup];

export default {
  name: 'UserGroupsSearchLegacy',

  inject: ['$validator'],

  components: {
    MessageContainer,
    SearchWithMultiSelectLegacy,
  },

  props: {
    name: {
      type: String,
      required: true,
    },
    previousValue: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Array,
      default: () => [],
    },
  },

  data: () => ({
    searchText: '',
    selectedValues: [],
    totalCount: null,
    loading: false,
    nextValueList: undefined,
    nextSearchTerm: undefined,
    containsDeletedGroups: false,
  }),

  computed: {
    ...mapGetters(userGroupCollection, [
      'getCollection',
      'getTotalCount',
    ]),

    errorMessage() {
      return getMessageForError(this.$validator.errors, this.name);
    },

    errorStyle() {
      return this.errorMessage ? this.$style.searchWithMultiSelectWithError : '';
    },

    options() {
      return this.getCollection.map(mapUserGroupToOption);
    },
  },

  watch: {
    value: {
      immediate: true,
      handler() {
        this.updateValueNames();
      },
    },
  },

  created() {
    // Debounce the external requests
    this.updateOptions = debounce(
      this.updateOptions,
      1000,
      { leading: true, trailing: true },
    );

    this.updateValueNames = debounce(
      this.updateValueNames,
      1000,
      { leading: true, trailing: true },
    );
  },

  methods: {
    ...mapActions(userGroupCollection, [
      'fetchUserGroupsById',
      'resetCollection',
      'setFilter',
      'setSearchTerm',
      'fetchCollection',
    ]),

    getMessageForError,

    /**
     * Handles changes to the search text
     */
    handleIncludedUsersFilter(value) {
      this.searchText = value;
      this.updateOptions();
    },

    /**
     * Handles changes to selected values
     */
    handleIncludedUsersInput(value) {
      // Go ahead and set selectedValues here so we don't have to lookup the name again
      this.selectedValues = value;
      const newValue = value.map(option => option.value);
      this.$emit('input', newValue);
    },

    async updateOptions() {
      this.loading = true;
      // setFilters also resets the collection
      this.setFilter({ filter: { name: 'type', operator: 'in', value: 'USER_GROUP' } });
      this.setSearchTerm({ searchTerm: this.searchText, preventReset: true });
      await this.fetchCollection({ populateActiveDirectory: false });
      this.loading = false;
    },

    /**
     * Get the names of the groups in value
     */
    async updateValueNames() {
      // Combine ids and remove duplicates
      const groupIds = [...new Set([...this.value, ...this.previousValue])];

      if (this.value.length === 0) {
        this.selectedValues = [];
      } else if (groupIds.every(id => this.selectedValues.some(group => group.value === id))) {
        // All group names have been populated so don't fetch them
      } else {
        try {
          const { results } = await this.fetchUserGroupsById({ ids: groupIds });
          // Set selectedValues triming deleted groups
          this.selectedValues = this.value
            .map(id => results.find(group => group.id === id))
            .filter(val => val != null)
            .map(mapUserGroupToOption);

          // If previousValue contains deleted groups we show a message
          this.containsDeletedGroups = !this.previousValue
            .every(id => results.some(group => group.id === id));

          // If value contains deleted groups we remove them
          if (!this.value.every(id => this.selectedValues.some(group => group.value === id))) {
            this.handleIncludedUsersInput(this.selectedValues);
          }
        } catch {
          // Nothing to handle here
        }
      }
    },
  },
};
</script>

<style module>
  @import "../../components/FormValidation/css/formFields.css";
  @import "../../components/FormValidation/css/FormValidationFields.css";

  @value (jcError) from '~css/brand-colors.css';

  .searchWithMultiSelectWithError {
    --errorColor: jcError;
  }

  .message {
    margin-top: 10px;
  }
</style>
